// @flow

import { css } from 'styled-components';
import merge from 'lodash/merge';
import media from '../../../helpers/media';
import type { Typography, TemplateProps, WysiwygTemplates } from '../../../types/theme';
import type { StyledComponentProps } from '../../../types/styledComponentProps';
import type { Theme } from '../../../types';

type Props = StyledComponentProps & {
  template: $Keys<WysiwygTemplates>,
  position: string,
  theme_variant: string,
};

export const generateTypographyCSS = (typography: Typography) => css`
  ${typography.fontSize && typography.fontSize.default && `font-size: ${typography.fontSize.default};`}
  ${typography.color && `color: ${typography.color};`}
  ${typography.lineHeight && `line-height: ${typography.lineHeight};`}
  ${typography.fontWeight && `font-weight: ${typography.fontWeight};`}
  ${typography.fontFamily && `font-family: ${typography.fontFamily};`}
  ${typography.textTransform && `text-transform: ${typography.textTransform};`}

  ${typography.fontSize
    && typography.fontSize.md
    && media.md(css`
      font-size: ${typography.fontSize.md};
    `)}
  ${typography.fontSize
    && typography.fontSize.lg
    && media.lg(css`
      font-size: ${typography.fontSize.lg};
    `)}
`;

export const generateTypographyCSSWithSelector = (selector: string, templateProps: TemplateProps) => css`
  ${/* sc-selector */selector} {
    ${generateTypographyCSS(templateProps[selector])}
  }
`;

const generateHeadings = (templateProps: TemplateProps) => css`
  ${['h1', 'h2', 'h3', 'h4', 'h5', 'h6']
    .map((heading: string) => generateTypographyCSSWithSelector(heading, templateProps))}
`;

const generateParagraph = (templateProps: TemplateProps) => css`
  p {
    ${generateTypographyCSS(templateProps.p)};
  }
`;

const defaultTemplate = (templateProps: TemplateProps) => css`
  ${generateHeadings(templateProps)};
  ${generateParagraph(templateProps)};
`;

const hero = (templateProps: TemplateProps) => css`
  padding-top: 0rem;
  padding-bottom: 0rem;
  text-shadow: .5px .5px 0 hsl(0,0%,44%);

  h1 {
    padding-bottom: ${(props: Props) => props.theme.wysiwyg.templates.hero.h1.paddingBottom};
  }

  ${defaultTemplate(templateProps)};
`;

const mergeTheme = (template: $Keys<WysiwygTemplates>, theme: Theme) => {
  if (template === 'default') {
    return theme.wysiwyg.templates.default;
  }

  return merge({}, theme.wysiwyg.templates.default, theme.wysiwyg.templates[template]);
};

const getTemplate = (template: string, templateProps: TemplateProps) => {
  switch (template) {
    case 'hero':
      return hero(templateProps);
    default:
      return defaultTemplate(templateProps);
  }
};

export default (template: $Keys<WysiwygTemplates>, theme: Theme) => (
  getTemplate(template, mergeTheme(template, theme))
);
